import type { DemoGrating, DemoPartialRun, DemoPartsSection } from "@/api";
import { Panel } from "@/components/Panel";
import { StateMessage } from "@/components/StateMessage";
import { StatusBadge } from "@/components/StatusBadge";
import { formatMm } from "@/lib/format";
import { groupElements } from "@/lib/elements";

import styles from "./PartsList.module.css";

/**
 * Elements and brackets per Teilzug, as the backend planned them.
 *
 * Two things are deliberately not on this screen. Prices: the price lists do
 * not exist yet, and an invented figure in front of the management is out of
 * the question (spec §1). And the per-section bracket count: it says what a
 * section would need standing alone, neighbouring sections share the bracket
 * between them, and a column of numbers that must not be added up will be
 * added up. The truthful figure is the one per Teilzug, and that is the one
 * shown.
 */

interface PartsListProps {
  runs: DemoPartialRun[];
  sections: DemoPartsSection[];
  grating: DemoGrating;
}

/** The backend's own sentence, hung onto a half sentence: "… — vom Kunden angegeben". */
function asClause(sentence: string): string {
  const trimmed = sentence.replace(/\.$/, "");
  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
}

function closureWord(run: DemoPartialRun): string {
  if (!run.closure.evaluable) return "Nicht prüfbar";
  if (run.closure.status === "conflict") return "Schließt nicht";
  return run.closure.sumHorizontalMm === 0 && run.closure.sumVerticalMm === 0
    ? "Schließt"
    : "Gerader Lauf";
}

export function PartsList({ runs, sections, grating }: PartsListProps) {
  if (runs.length === 0) {
    return (
      <Panel title="Stückliste">
        <StateMessage title="Keine Teilzüge">
          Aus den gelesenen Maßen ließ sich kein Rostlauf bilden. Die Hinweise unten nennen den
          Grund.
        </StateMessage>
      </Panel>
    );
  }

  return (
    <div className={styles.parts}>
      {runs.map((run) => {
        const own = sections.filter((section) => section.runId === run.id);

        return (
          <Panel
            key={run.id}
            title={run.label}
            hint={`${own.length} ${own.length === 1 ? "Abschnitt" : "Abschnitte"} · ${
              run.totals.stretches === 1
                ? "ein durchgehender Rostlauf"
                : `${run.totals.stretches} durchgehende Rostläufe`
            }`}
            aside={<StatusBadge status={run.closure.status}>{closureWord(run)}</StatusBadge>}
            flush
          >
            <div className={styles.tableWrap}>
              <table className={styles.partsTable}>
                <caption className="visually-hidden">
                  Abschnitte des {run.label} mit Aufteilung in Elemente und den
                  Rohlingen, die daraus zugeschnitten werden
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="label">
                      Abschnitt
                    </th>
                    <th scope="col" className="label">
                      Aufteilung
                    </th>
                    <th scope="col" className="label">
                      Rohling
                    </th>
                    <th scope="col" className="label">
                      Elemente
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {own.map((section, index) => (
                    <tr key={`${section.runId}-${index}`}>
                      <th scope="row" className={`num ${styles.sectionLength}`}>
                        {formatMm(section.lengthMm)}
                        {section.mergedFrom.length > 0 ? (
                          <span className={styles.merged}>
                            zusammengelegt aus{" "}
                            {section.mergedFrom.map((part) => formatMm(part)).join(" + ")}
                          </span>
                        ) : null}
                      </th>
                      <td>
                        <span className={styles.splitCell}>
                          {groupElements(section.elements).map((group, position) => (
                            <span key={`${group.lengthMm}-${position}`} className={styles.group}>
                              {group.count > 1 ? (
                                <>
                                  <span className="num">{group.count}</span> ×{" "}
                                </>
                              ) : null}
                              <span className="num">{formatMm(group.lengthMm)}</span>
                            </span>
                          ))}
                        </span>
                      </td>
                      <td className={styles.blankCell}>
                        {section.blanks && section.blanks.length > 0 ? (
                          <span className={styles.splitCell}>
                            {groupElements(section.blanks).map((group, position) => (
                              <span
                                key={`blank-${group.lengthMm}-${position}`}
                                className={styles.group}
                              >
                                {group.count > 1 ? (
                                  <>
                                    <span className="num">{group.count}</span> ×{" "}
                                  </>
                                ) : null}
                                <span className="num">{formatMm(group.lengthMm)}</span>
                              </span>
                            ))}
                          </span>
                        ) : (
                          // No mitre anywhere in this section: the blank *is*
                          // the wall stretch, and repeating the column beside
                          // itself would only invite the reader to look for a
                          // difference that is not there.
                          <span className={styles.sameAsSplit}>wie Aufteilung</span>
                        )}
                      </td>
                      <td className={`num ${styles.count}`}>{section.elements.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <dl className={styles.runTotals}>
              <div>
                <dt className="label">Elemente</dt>
                <dd className="num">{run.totals.elements}</dd>
              </div>
              <div>
                <dt className="label">Konsolen</dt>
                <dd className="num">{run.totals.consoles}</dd>
              </div>
              <div>
                <dt className="label">Länge</dt>
                <dd className="num">{formatMm(run.totals.lengthMm)}</dd>
              </div>
            </dl>

            <p className={styles.closureNote}>{run.closure.message}</p>
          </Panel>
        );
      })}

      <p className={styles.grating}>
        {grating.label ? (
          <>
            <strong>
              Rosttyp {grating.label}
              {grating.widthMm !== null ? (
                <>
                  {" "}
                  <span className="num">{formatMm(grating.widthMm)}</span>
                </>
              ) : null}
            </strong>{" "}
            — {asClause(grating.note)}
          </>
        ) : (
          <strong>{grating.note}</strong>
        )}
      </p>
    </div>
  );
}
